import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import Base64 from "@/utils/urlUtils/Base64.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NestConstants from "@/utils/constants/NestConstants.ts";
import MetaData from "@/utils/common/MetaData.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {FieldValue} from "@/utils/presenters/interfaces.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import FieldToFocusUtils from "@/utils/common/FieldToFocusUtils.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import EntityReferenceData = namaTypes.EntityReferenceData;
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";

export default class FieldValuesSetter {
  presenter: EditPresenter;
  values: FieldValue[];
  fieldValues: string
  gridsToRefresh: Set<string> = new Set<string>();

  constructor(encodedValues: string, presenter: EditPresenter) {
    this.presenter = presenter
    this.values = [];
    this.fieldValues = encodedValues;
    if (encodedValues.startsWith(CommonConstants.BASE64_PREFIX)) {
      this.fieldValues = Base64.decode(encodedValues.substring(CommonConstants.BASE64_PREFIX.length));
      //do not remove this log statement, we need it to review generated creator
      console.log(this.fieldValues);
    }
  }

  start() {
    let fieldValues: string = this.fieldValues;
    let fieldsStr: string[] = fieldValues.split("--");
    for (let fieldStr of fieldsStr) {
      let parts: string[] = fieldStr.split(":=");
      let value: FieldValue = {};
      value.id = parts[0];
      if (!value.id)
        continue;
      if (value.id.includes(">"))
        value.id = value.id.replace('>', '#');
      this.values.push(value);
      if (parts.length > 1) {
        if (parts[1].includes(":@")) {
          let subValue = parts[1].split(":@");
          value.value = subValue[0];
          if (subValue.length > 1)
            value.rowNumber = subValue[1];
        } else {
          value.value = parts[1];
        }
      }
    }
    this.schedule();
  }

  private schedule() {
    setTimeout(async () => {
      let value: Record<string, any> = this.values[0];
      this.values.splice(0, 1);
      await this.prepareAndSetValue(value);
      if (!this.values.length) {
        await this.finished();
        return;
      }
      this.schedule();
    }, 1);
  }

  private async prepareAndSetValue(value: FieldValue) {
    if (ObjectChecker.isNullOrUndefined(value))
      return;
    let genRefType: string = '';
    let genRefSelector: string = '';
    let fieldId: string = value.id ?? '';
    if (fieldId.includes("#")) {
      let parts = fieldId.split("#");
      fieldId = parts[0];
      if (parts[1].toLowerCase() === "entityType" || parts[1].toLowerCase() === "type")
        genRefType = parts[1];
      if (parts[1].toLowerCase() === "value" || parts[1].toLowerCase() === "code")
        genRefSelector = parts[1];
    }
    let fieldMetadata: FieldMetaData | undefined = MetaData.findFieldMetadata(fieldId, this.presenter.fetchMetadata());
    if (!fieldMetadata)
      return;
    let row: number = -1;
    let grid: any[] = [];
    let gridId = MetaData.getFieldParentDetail(this.presenter.fetchMetadata(), fieldId)
    if (gridId) {
      grid = NamaCommon.getFieldValue(this.presenter.fetchRecord(), gridId);
      this.gridsToRefresh.add(gridId);
    }
    if (value.rowNumber === "@@last")
      row = grid.length;
    else if (value.rowNumber === "@@end")
      row = AGGridUtils.isEmptyLine(grid[grid.length - 1], true) ? grid.length : grid.length + 1;
    else if (value.rowNumber && +value.rowNumber > 0)
      row = +value.rowNumber;
    if (row > 0)
      row = row - 1;
    if (gridId && row >= 0)
      this.presenter.updateGridSelectedLine(gridId, row);
    let fieldValue: any = value.value;
    if (value.id === NestConstants.CALL_GUI_ACTION) {
      await this.callGUIAction(value.value ?? '');
    }
    let fieldType = fieldMetadata.fieldType;
    if (fieldType === FieldType.Boolean)
      fieldValue = (fieldValue === "true" || fieldValue === "1");
    if (fieldType === FieldType.Date || fieldType === FieldType.DateAndTime || fieldType === FieldType.Time)
      fieldValue = FieldTypesUtils.parseString(fieldValue, fieldType);
    if (fieldType === FieldType.Genericreference && genRefType) {
      fieldValue = {entityType: fieldValue};
        await this.setValueInfieldAndCallPostAction(fieldValue, row, fieldId, genRefType, grid);
        return;
    }
    if (fieldType === FieldType.Reference || (fieldType === FieldType.Genericreference && genRefSelector)) {
      let refEntityType = FieldValuesSetter.calcReferenceEntityType(this.fetchCurrentRefEntityType(fieldId, row), fieldMetadata);
      if (!fieldValue)
        fieldValue = null;
      else
        fieldValue = await this.searchForReference(fieldId, fieldValue, refEntityType ?? '');
    }
      await this.setValueInfieldAndCallPostAction(fieldValue, row, fieldId, genRefType, grid);
      return;
  }

  private static calcReferenceEntityType(currentEntityType: string, fieldMetaData: FieldMetaData) {
    if (currentEntityType)
      return currentEntityType;
    if (fieldMetaData.referencedEntityType)
      return fieldMetaData.referencedEntityType;
    if (fieldMetaData.defaultEntityType)
      return fieldMetaData.defaultEntityType;
    if (fieldMetaData.allowedValues && fieldMetaData.allowedValues.length === 1)
      return fieldMetaData.allowedValues[0];
  }

  private async callGUIAction(actionId: string) {
    await this.presenter.callGUIAction(actionId);
  }

  private fetchCurrentRefEntityType(fieldId: string, row: number) {
    if (!this.presenter)
      return undefined;
    let fieldValue = NamaCommon.getFieldValue(this.presenter.fetchRecord(), fieldId, row);
    if (fieldValue && fieldValue.entityType)
      return fieldValue.entityType;
    return undefined;
  }

  private async setValueInfieldAndCallPostAction(fieldValue: any, rowNumber: number, fieldId: string, genRefType: string, grid: any[]) {
    let fieldsToFocusUtils = new FieldToFocusUtils(this.presenter);
    let fieldToSet = fieldId;
    let oldValue = NamaCommon.getFieldValue(this.presenter.fetchRecord(), fieldToSet, rowNumber);
    if (rowNumber > -1) {
      if (grid && rowNumber >= grid.length && AGGridUtils.canAppendRow(this.presenter, fieldId)) {
        let id = await ClientCache.asyncNextGUIDId();
        grid.push({id, g2Id: id});
      }
    }
    NamaCommon.setFieldValue(this.presenter.fetchRecord(), fieldToSet, fieldValue, rowNumber);
    await FieldToFocusUtils.focusToField(fieldsToFocusUtils, fieldId, rowNumber);
    let newValue = NamaCommon.getFieldValue(this.presenter.fetchRecord(), fieldToSet, rowNumber);
    if (ObjectChecker.areEqual(oldValue, newValue))
      return;
    await this.callPostAction(fieldId, rowNumber);
  }

  private async callPostAction(fieldId: string, rowIndex: number) {
    await useLoadingStore().awaitLoadingFinish(1)
    useLoadingStore().startLoading();
    if (this.presenter.fetchDispatcher()) {
      if (rowIndex > -1)
        await this.presenter.fetchDispatcher().validateColumn(fieldId, rowIndex, false);
      else
        await this.presenter.fetchDispatcher().validate(fieldId)
    }
    useLoadingStore().stopLoading();
  }

  private async searchForReference(fieldId: string, refCode: string, refEntityType: string): Promise<EntityReferenceData | undefined> {
    let searchContextInfo = this.presenter.createSearchContextInfo(fieldId, -1);
    return await EditScreenFieldUtils.findRefByCode(refEntityType, refCode, searchContextInfo);
  }

  private async finished() {
    let fieldsToFocusUtils = new FieldToFocusUtils(this.presenter);
    for (let gridId of this.gridsToRefresh) {
      await FieldToFocusUtils.focusToField(fieldsToFocusUtils, gridId, IdsAndConstants.MRN_REFRESH_CELLS);
    }
  }
}
